import { CopyIcon, ProCard } from '@components'
import cn from '@integrations/classnames'
import { type HTMLAttributes, type ReactNode, useState } from '@integrations/react'
import { uiSize } from '@utils'
import { copyTextToClipboard, tr } from 'mmfintech-commons'
import styles from './SidePanel.module.scss'

/** Detail panel beside a list: header, scrolling body of sections and label/value rows. */
function SidePanel({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <ProCard className={cn('side-panel', className)} {...props}>
      {children}
    </ProCard>
  )
}

interface HeaderProps {
  eyebrow?: ReactNode
  title: ReactNode
  icon?: ReactNode
  onClose: () => void
  closeTestId?: string
}

function Header({ eyebrow, title, icon, onClose, closeTestId }: HeaderProps) {
  return (
    <div className={cn(styles['side-panel__head'], 'side-panel__head')}>
      <div className={cn(styles['side-panel__heading'], 'side-panel__heading')}>
        {icon && <span className={cn(styles['side-panel__icon'], 'side-panel__icon')}>{icon}</span>}
        <div className={cn(styles['side-panel__titles'], 'side-panel__titles')}>
          {eyebrow && <div className={cn(styles['side-panel__eyebrow'], 'side-panel__eyebrow')}>{eyebrow}</div>}
          <div className={cn(styles['side-panel__title'], 'side-panel__title')}>{title}</div>
        </div>
      </div>
      <button
        type='button'
        className={cn(styles['side-panel__close'], 'side-panel__close')}
        aria-label={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
        onClick={onClose}
        data-test={closeTestId}
      >
        <svg
          width={uiSize(10)}
          height={uiSize(10)}
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='3'
        >
          <path d='M5 5l14 14M19 5 5 19' />
        </svg>
      </button>
    </div>
  )
}

function Body({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['side-panel__body'], 'side-panel__body', className)} {...props}>
      {children}
    </div>
  )
}

interface SectionProps {
  title: ReactNode
  aside?: ReactNode
  children: ReactNode
}

function Section({ title, aside, children }: SectionProps) {
  return (
    <section className={cn(styles['side-panel__section'], 'side-panel__section')}>
      <div className={cn(styles['side-panel__section-head'], 'side-panel__section-head')}>
        <span className={cn(styles['side-panel__section-title'], 'side-panel__section-title')}>{title}</span>
        {aside && <span className={cn(styles['side-panel__section-aside'], 'side-panel__section-aside')}>{aside}</span>}
      </div>
      {children}
    </section>
  )
}

interface RowProps {
  label: ReactNode
  children: ReactNode
  /** Plain value to copy; renders a copy button when set. */
  copyValue?: string
  tone?: 'default' | 'success' | 'error'
  'data-test'?: string
}

function Row({ label, children, copyValue, tone = 'default', ...props }: RowProps) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    copyTextToClipboard(copyValue)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={cn(styles['side-panel__row'], 'side-panel__row')}>
      <span className={cn(styles['side-panel__label'], 'side-panel__label')}>{label}</span>
      <span
        className={cn(styles['side-panel__value'], 'side-panel__value', {
          [styles[`side-panel__value--${tone}`]]: tone !== 'default'
        })}
        data-test={props['data-test']}
      >
        {children}
      </span>
      {copyValue && (
        <button
          type='button'
          className={cn(styles['side-panel__copy'], 'side-panel__copy')}
          aria-label={tr('FRONTEND.ACCOUNTS.INFO.COPY', 'Copy {{FIELD}}', { FIELD: String(label) })}
          onClick={handleCopy}
        >
          {copied ? '✓' : <CopyIcon color='currentColor' variant='compact' />}
        </button>
      )}
    </div>
  )
}

/** Buttons closing a panel body (edit, delete, open the full record). */
function Actions({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['side-panel__actions'], 'side-panel__actions', className)} {...props}>
      {children}
    </div>
  )
}

SidePanel.Header = Header
SidePanel.Actions = Actions
SidePanel.Body = Body
SidePanel.Section = Section
SidePanel.Row = Row

export default SidePanel
